import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLoanCalculator } from "@/hooks/useLoanCalculator";
import { ArrowRight, TrendingUp, TrendingDown, DollarSign, Info } from "lucide-react";
import { Link } from 'react-router-dom';
import { toast } from "sonner";

const PRICE_TRENDS = {
  "pääkaupunkiseutu": 3.2, // Prosenttia vuodessa
  "kasvukeskukset": 1.8,
  "maaseutu": -0.5
};

const MARKET_CONFIDENCE = {
  "ostaminen": 65, // 0-100 skaalalla
  "myyminen": 72
};

const AsuntomarkkinatLaskuri = () => {
  const [tab, setTab] = useState<string>("asuntosijoitus");
  const [asunnonHinta, setAsunnonHinta] = useState<number>(250000);
  const [sijoitusaika, setSijoitusaika] = useState<number>(5);
  const [alue, setAlue] = useState<"pääkaupunkiseutu" | "kasvukeskukset" | "maaseutu">("pääkaupunkiseutu");
  const [nykyinenVuokra, setNykyinenVuokra] = useState<number>(1200);
  const [lainanMaara, setLainanMaara] = useState<number>(200000);
  const [lainaAika, setLainaAika] = useState<number>(20);
  
  const lainaLaskelma = useLoanCalculator(lainanMaara, lainaAika, "asuntolaina");
  
  const laskeTulevaArvo = () => {
    const vuotuinenArvonmuutos = PRICE_TRENDS[alue] / 100;
    return Math.round(asunnonHinta * Math.pow((1 + vuotuinenArvonmuutos), sijoitusaika));
  };
  
  const laskeVuokraVsOmistaminen = () => {
    const kuukausieraYhteensa = lainaLaskelma.monthlyPayment * 12 * lainaAika;
    const vuokraYhteensa = nykyinenVuokra * 12 * lainaAika;
    const erotus = vuokraYhteensa - kuukausieraYhteensa;
    
    return {
      kuukausieraYhteensa,
      vuokraYhteensa,
      erotus
    };
  };
  
  const tulevaArvo = laskeTulevaArvo();
  const arvonMuutos = tulevaArvo - asunnonHinta;
  const vuokraVsOmistaminen = laskeVuokraVsOmistaminen();
  
  const onKopioi = () => {
    const tulosText = `Asuntomarkkinat 2025 - Laskuritulos:
    Asunnon nykyinen hinta: ${asunnonHinta.toLocaleString('fi-FI')} €
    Asunnon arvioitu hinta ${sijoitusaika} vuoden päästä: ${tulevaArvo.toLocaleString('fi-FI')} €
    Arvonmuutos: ${arvonMuutos.toLocaleString('fi-FI')} € (${((arvonMuutos / asunnonHinta) * 100).toFixed(1)}%)`;
    
    navigator.clipboard.writeText(tulosText).then(() => {
      toast.success("Laskelma kopioitu leikepöydälle!");
    });
  };
  
  return (
    <Card className="w-full shadow-lg">
      <CardHeader className="bg-blue-50 border-b">
        <CardTitle className="text-blue-800">Asuntomarkkinoiden laskuri 2025</CardTitle>
        <CardDescription>
          Laske asuntosijoituksen kannattavuus tai vertaa vuokralla asumista omistusasumiseen
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <Tabs defaultValue="asuntosijoitus" value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 mb-6">
            <TabsTrigger value="asuntosijoitus">Asunnon arvonkehitys</TabsTrigger>
            <TabsTrigger value="vuokraVsOmistus">Vuokra vs. omistus</TabsTrigger>
          </TabsList>
          
          <TabsContent value="asuntosijoitus" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="asunnonHinta">Asunnon nykyinen hinta (€)</Label>
                <Input
                  id="asunnonHinta"
                  type="number"
                  value={asunnonHinta}
                  onChange={(e) => setAsunnonHinta(Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="sijoitusaika">Sijoitusaika (vuosia)</Label>
                <Input
                  id="sijoitusaika"
                  type="number"
                  value={sijoitusaika}
                  onChange={(e) => setSijoitusaika(Number(e.target.value))}
                  className="mt-1"
                  min={1}
                  max={30}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="alue">Asunnon sijainti</Label>
              <select
                id="alue"
                value={alue}
                onChange={(e) => setAlue(e.target.value as "pääkaupunkiseutu" | "kasvukeskukset" | "maaseutu")}
                className="w-full p-2 rounded-md border mt-1"
              >
                <option value="pääkaupunkiseutu">Pääkaupunkiseutu</option>
                <option value="kasvukeskukset">Muut kasvukeskukset</option>
                <option value="maaseutu">Maaseutu/Muut alueet</option>
              </select>
            </div>
            
            <Separator className="my-4" />
            
            <div className="bg-blue-50 p-4 rounded-lg space-y-3">
              <div className="flex justify-between">
                <span className="font-medium">Nykyinen hinta:</span>
                <span>{asunnonHinta.toLocaleString('fi-FI')} €</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Arvioitu hinta {sijoitusaika} vuoden päästä:</span>
                <span className="font-semibold text-blue-700">{tulevaArvo.toLocaleString('fi-FI')} €</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Arvonmuutos:</span>
                <span className={arvonMuutos >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                  {arvonMuutos.toLocaleString('fi-FI')} € ({((arvonMuutos / asunnonHinta) * 100).toFixed(1)}%)
                  {arvonMuutos >= 0 ? <TrendingUp className="inline ml-1 h-4 w-4" /> : <TrendingDown className="inline ml-1 h-4 w-4" />}
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-gray-200">
                <span>Perustuu alueen {alue} hintatrendiin: {PRICE_TRENDS[alue] > 0 ? "+" : ""}{PRICE_TRENDS[alue].toFixed(1)}% / vuosi</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <Button onClick={onKopioi} variant="outline" className="flex-1">
                Kopioi laskelma
              </Button>
              <Button asChild className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium">
                <Link to="/kilpailuta" className="flex items-center justify-center">
                  <span className="mr-1">Kilpailuta asuntolaina</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="vuokraVsOmistus" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lainanMaara">Lainan määrä (€)</Label>
                <Input
                  id="lainanMaara"
                  type="number"
                  value={lainanMaara}
                  onChange={(e) => setLainanMaara(Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="lainaAika">Laina-aika (vuosia)</Label>
                <Input
                  id="lainaAika"
                  type="number"
                  value={lainaAika}
                  onChange={(e) => setLainaAika(Number(e.target.value))}
                  className="mt-1"
                  min={5}
                  max={35}
                />
              </div>
              <div>
                <Label htmlFor="nykyinenVuokra">Nykyinen vuokra (€/kk)</Label>
                <Input
                  id="nykyinenVuokra"
                  type="number"
                  value={nykyinenVuokra}
                  onChange={(e) => setNykyinenVuokra(Number(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>
            
            <Separator className="my-4" />
            
            <div className="bg-blue-50 p-4 rounded-lg space-y-3">
              <div className="flex justify-between">
                <span className="font-medium">Asuntolainan kuukausierä:</span>
                <span className="font-semibold">{lainaLaskelma.monthlyPayment.toLocaleString('fi-FI')} €/kk</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Nykyinen vuokra:</span>
                <span>{nykyinenVuokra.toLocaleString('fi-FI')} €/kk</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between">
                <span className="font-medium">Lainan kokonaiskustannus ({lainaAika} vuotta):</span>
                <span>{vuokraVsOmistaminen.kuukausieraYhteensa.toLocaleString('fi-FI')} €</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Vuokran kokonaiskustannus ({lainaAika} vuotta):</span>
                <span>{vuokraVsOmistaminen.vuokraYhteensa.toLocaleString('fi-FI')} €</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Erotus:</span>
                <span className={vuokraVsOmistaminen.erotus >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                  {vuokraVsOmistaminen.erotus >= 0 ? "+" : ""}{vuokraVsOmistaminen.erotus.toLocaleString('fi-FI')} €
                  {vuokraVsOmistaminen.erotus >= 0 ? <DollarSign className="inline ml-1 h-4 w-4" /> : <TrendingDown className="inline ml-1 h-4 w-4" />}
                </span>
              </div>
              <div className="flex items-center text-xs text-gray-500 mt-2 pt-2 border-t border-gray-200">
                <Info className="h-3 w-3 mr-1" />
                <span>Positiivinen erotus tarkoittaa, että omistusasuminen tulee edullisemmaksi pitkällä aikavälillä</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <Button asChild className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium">
                <Link to="/lainat/asuntolaina" className="flex items-center justify-center">
                  <span className="mr-1">Lue lisää asuntolainoista</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium">
                <Link to="/kilpailuta" className="flex items-center justify-center">
                  <span>Kilpailuta lainasi nyt</span>
                </Link>
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AsuntomarkkinatLaskuri;
